// src/components/dashboard/delete-venue-confirm.tsx
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';

interface DeleteVenueConfirmProps {
  venueId: string;
  venueName: string;
  onClose: () => void;
}

export default function DeleteVenueConfirm({ venueId, venueName, onClose }: DeleteVenueConfirmProps) {
  const c = useAppColors();
  const [step, setStep] = useState<1 | 2>(1);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = typed.trim() === venueName;

  const handleDelete = async () => {
    if (!matches || deleting) return;
    setDeleting(true);
    setError(null);
    const { data, error: fnError } = await supabase.functions.invoke('venues', {
      body: { action: 'delete', venue_id: venueId, confirm_name: typed.trim() },
    });
    if (fnError || !data?.ok) {
      setDeleting(false);
      setError((data as { error?: string })?.error ?? 'Could not delete venue.');
      return;
    }
    window.location.reload();
  };

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[2000] flex items-center justify-center p-5"
        style={{ background: 'rgba(0,0,0,0.65)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-[360px] rounded-2xl overflow-hidden"
          style={{ background: c.bg, border: `1px solid ${c.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}
        >
          <div className="p-5">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
              style={{ background: 'rgba(232,116,106,0.12)' }}
            >
              <AlertTriangle size={18} style={{ color: '#E8746A' }} />
            </div>

            {step === 1 ? (
              <>
                <h3 className="text-base font-semibold mb-1.5" style={{ color: c.text }}>Delete venue?</h3>
                <p className="text-sm leading-relaxed" style={{ color: c.textDim }}>
                  This removes <strong style={{ color: c.text }}>{venueName}</strong> and everyone's access to it — tasks, documents, roster, chat history, everything. This can't be undone from the app.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-base font-semibold mb-1.5" style={{ color: c.text }}>Confirm deletion</h3>
                <p className="text-sm leading-relaxed mb-3" style={{ color: c.textDim }}>
                  Type <strong style={{ color: c.text }}>{venueName}</strong> to confirm.
                </p>
                <input
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(); }}
                  placeholder={venueName}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                  style={{
                    background: c.iconBg,
                    border: `1px solid ${matches ? '#E8746A' : c.border}`,
                    color: c.text,
                    caretColor: c.text,
                  }}
                />
                {error && <p className="text-[12px] mt-2" style={{ color: '#E8746A' }}>{error}</p>}
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 p-4 pt-0">
            {step === 1 ? (
              <>
                <button
                  onClick={() => setStep(2)}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#E8746A', color: '#fff' }}
                >
                  Continue
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-2.5 rounded-lg text-sm font-medium"
                  style={{ background: 'transparent', color: c.textDim }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleDelete}
                  disabled={!matches || deleting}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-40"
                  style={{ background: '#E8746A', color: '#fff' }}
                >
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </button>
                <button
                  onClick={() => setStep(1)}
                  className="w-full py-2.5 rounded-lg text-sm font-medium"
                  style={{ background: 'transparent', color: c.textDim }}
                >
                  Back
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}